import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import type { IncidentPosition } from 'hooks/incident/useRoster';
import { getDescendantIds } from './getDescendantIds';
import type { RosterOccupancyOption } from '../RosterOrgChart';
import { POSITION_CATEGORIES } from '../positionCategories';

export type RosterFilters = {
  search: string;
  positionIds: string[];
  categories: string[];
  occupancy: RosterOccupancyOption[];
  /**
   * HWCG window to draw. Omitted means the current window. Flag-off roster
   * views leave this unset and keep the draft-roster Next OP filter.
   */
  hwcgPeriod?: 'current' | 'all' | number;
};

const DEFAULT_OCCUPANCY: RosterOccupancyOption[] = ['active', 'draft', 'empty'];

export const EMPTY_ROSTER_FILTERS: RosterFilters = {
  search: '',
  positionIds: [],
  categories: [],
  occupancy: [...DEFAULT_OCCUPANCY],
};

const GROUP_CATEGORIES = new Set(['Section', 'Branch', 'Division', 'Group']);

/**
 * The Group this seat sits in. A Group seat is its own group. A seat with no
 * Group ancestor stays itself, so the filter still shows that seat and the
 * positions under it.
 */
export function containingGroupId(
  positions: ReadonlyArray<
    Pick<IncidentPosition, 'id' | 'parent_id' | 'category'>
  >,
  positionId: string
): string {
  const byId = new Map(positions.map((position) => [position.id, position]));
  const seen = new Set<string>();
  let current = byId.get(positionId);
  while (current && !seen.has(current.id)) {
    if (current.category === 'Group') return current.id;
    seen.add(current.id);
    current = current.parent_id ? byId.get(current.parent_id) : undefined;
  }
  return positionId;
}

/** One Group per assigned seat, with duplicates removed. */
export function containingGroupIds(
  positions: ReadonlyArray<
    Pick<IncidentPosition, 'id' | 'parent_id' | 'category'>
  >,
  positionIds: readonly string[]
): string[] {
  const ids = new Set<string>();
  positionIds.forEach((positionId) => {
    ids.add(containingGroupId(positions, positionId));
  });
  return [...ids];
}

/**
 * ICS seats the signed-in user holds. Other members' seats are ignored, and a
 * user with no seat yields an empty list so the roster stays unfiltered.
 */
export function assignedPositionIds(
  roster: ReadonlyArray<{
    userId: string;
    incidentPositions: ReadonlyArray<{ positionId: string }>;
  }>,
  userId: string | null | undefined
): string[] {
  if (!userId) return [];
  const ids = new Set<string>();
  roster.forEach((entry) => {
    if (entry.userId !== userId) return;
    entry.incidentPositions.forEach((seat) => {
      if (seat.positionId) ids.add(seat.positionId);
    });
  });
  return [...ids];
}

/**
 * The chosen position plus every descendant, so a Branch filter keeps its
 * Groups.
 */
export function subtreeIds(
  positions: IncidentPosition[],
  positionId: string
): Set<string> {
  const ids = getDescendantIds(positions, positionId);
  ids.add(positionId);
  return ids;
}

export function unionSubtrees(
  positions: IncidentPosition[],
  positionIds: string[]
): Set<string> {
  const ids = new Set<string>();
  positionIds.forEach((positionId) => {
    subtreeIds(positions, positionId).forEach((id) => ids.add(id));
  });
  return ids;
}

/** The signed-in user's seats and every position that reports up through them. */
export function seatAndDescendantIds(
  positions: IncidentPosition[],
  seatIds: readonly string[]
): string[] {
  return [...unionSubtrees(positions, [...seatIds])];
}

export function positionMatchesSearch(
  position: IncidentPosition,
  search: string
): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return (
    position.name.toLowerCase().includes(term) ||
    (position.category ?? '').toLowerCase().includes(term) ||
    (position.custom_category ?? '').toLowerCase().includes(term)
  );
}

/** US-IR007: match roster members by name, email, or assigned position title. */
export function rosterEntryMatchesSearch(
  entry: Pick<
    RosterEntry,
    'userName' | 'emailAddress' | 'profileSettings' | 'incidentPositions'
  >,
  search: string
): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  const name = entry.userName || entry.profileSettings?.userName || '';
  const email = entry.emailAddress || '';
  if (name.toLowerCase().includes(term) || email.toLowerCase().includes(term)) {
    return true;
  }
  return (entry.incidentPositions ?? []).some((seat) =>
    (seat.positionName ?? '').toLowerCase().includes(term)
  );
}

export function positionMatchesRosterFilters(
  position: IncidentPosition,
  filters: RosterFilters,
  positions: IncidentPosition[]
): boolean {
  if (!positionMatchesSearch(position, filters.search)) return false;
  if (filters.categories.length > 0) {
    if (!filters.categories.includes(position.category)) return false;
  }
  if (filters.positionIds.length > 0) {
    return unionSubtrees(positions, filters.positionIds).has(position.id);
  }
  return true;
}

export function filterRosterPositions(
  positions: IncidentPosition[],
  filters: RosterFilters
): IncidentPosition[] {
  return positions.filter((position) =>
    positionMatchesRosterFilters(position, filters, positions)
  );
}

export function hasRosterNarrowingFilters(filters: RosterFilters): boolean {
  return (
    filters.search.trim().length > 0 ||
    filters.positionIds.length > 0 ||
    filters.categories.length > 0 ||
    filters.occupancy.length !== DEFAULT_OCCUPANCY.length ||
    DEFAULT_OCCUPANCY.some((option) => !filters.occupancy.includes(option)) ||
    (filters.hwcgPeriod != null && filters.hwcgPeriod !== 'current')
  );
}

export type RosterFilterChip = {
  id: string;
  label: string;
};

const OCCUPANCY_LABEL: Record<RosterOccupancyOption, string> = {
  active: 'Current OP',
  draft: 'Next OP',
  empty: 'Empty Positions',
};

export function buildRosterFilterChips(
  filters: RosterFilters,
  positions: IncidentPosition[]
): RosterFilterChip[] {
  const chips: RosterFilterChip[] = [];
  const search = filters.search.trim();
  if (search) chips.push({ id: 'search', label: `Search: ${search}` });
  filters.positionIds.forEach((id) => {
    const position = positions.find((item) => item.id === id);
    chips.push({
      id: `position:${id}`,
      label: `${position?.name ?? id} and subordinates`,
    });
  });
  filters.categories.forEach((category) => {
    chips.push({ id: `category:${category}`, label: `Category: ${category}` });
  });
  if (filters.hwcgPeriod === 'all') {
    chips.push({ id: 'hwcg-period', label: 'All' });
  } else if (typeof filters.hwcgPeriod === 'number') {
    chips.push({ id: 'hwcg-period', label: `OP ${filters.hwcgPeriod}` });
  }
  if (
    hasRosterNarrowingFilters({
      ...filters,
      search: '',
      positionIds: [],
      categories: [],
      hwcgPeriod: undefined,
    })
  ) {
    filters.occupancy.forEach((option) => {
      chips.push({
        id: `occupancy:${option}`,
        label: OCCUPANCY_LABEL[option],
      });
    });
  }
  return chips;
}

export function dismissRosterFilterChip(
  filters: RosterFilters,
  chipId: string
): RosterFilters {
  if (chipId === 'search') return { ...filters, search: '' };
  if (chipId.startsWith('position:')) {
    const id = chipId.slice('position:'.length);
    return {
      ...filters,
      positionIds: filters.positionIds.filter((value) => value !== id),
    };
  }
  if (chipId.startsWith('category:')) {
    const category = chipId.slice('category:'.length);
    return {
      ...filters,
      categories: filters.categories.filter((value) => value !== category),
    };
  }
  if (chipId === 'hwcg-period') return { ...filters, hwcgPeriod: 'current' };
  if (chipId.startsWith('occupancy:')) {
    const option = chipId.slice('occupancy:'.length) as RosterOccupancyOption;
    const next = filters.occupancy.filter((value) => value !== option);
    return {
      ...filters,
      occupancy: next.length === 0 ? filters.occupancy : next,
    };
  }
  return filters;
}

export function toggleRosterFilterValue(
  values: string[],
  value: string
): string[] {
  return values.includes(value)
    ? values.filter((existing) => existing !== value)
    : [...values, value];
}

const CATEGORY_ORDER = POSITION_CATEGORIES as readonly string[];

export function rosterFilterPositionOptions(
  positions: IncidentPosition[]
): { value: string; label: string; group: string }[] {
  return [...positions]
    .sort((a, b) => {
      const groupA = GROUP_CATEGORIES.has(a.category) ? 0 : 1;
      const groupB = GROUP_CATEGORIES.has(b.category) ? 0 : 1;
      if (groupA !== groupB) return groupA - groupB;
      const cat =
        CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
      if (cat !== 0) return cat;
      return a.name.localeCompare(b.name);
    })
    .map((position) => ({
      value: position.id,
      label: position.name,
      group: GROUP_CATEGORIES.has(position.category)
        ? position.category
        : 'Other positions',
    }));
}

export function rosterFilterCategoryOptions(
  positions: IncidentPosition[]
): { value: string; label: string }[] {
  const seen = new Set(positions.map((position) => position.category));
  return CATEGORY_ORDER.filter((category) => seen.has(category)).map(
    (category) => ({ value: category, label: category })
  );
}

export const ROSTER_OCCUPANCY_OPTIONS: {
  value: RosterOccupancyOption;
  label: string;
}[] = [
  { value: 'active', label: 'Current OP' },
  { value: 'draft', label: 'Next OP' },
  { value: 'empty', label: 'Empty Positions' },
];
